import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MarkSubmissionConfirmModal from "./MarkSubmissionConfirmModal";
import { useSubject } from "../../context/SubjectContext";
import { useCenter } from "../../context/CenterContext";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";
import toast from "react-hot-toast";
import {
  useModerationWindowSession,
  MARK_STATUS,
} from "../../constants/Constants";
import {
  handleDownload,
  handleDownloadStage3,
  checkunitIsConsortiaUnit,
  getCenterType,
} from "../../utils/utils";
import { useApi } from "../../context/ApiClient";

const MarkSubmissionHeader = ({
  onSubmitClicked,
  showModal,
  setShowModal,
  rows,
  candidates,
  isRowComplete,
  onManualSaveSuccess,
  lastSavedAt,
  isStage3 = false,
  isPostStage3 = false,
  hasUnsavedChanges,
  STORAGE_KEY,
  filenamePrefix,
}) => {

  const moderationWindowSession = useModerationWindowSession();
  const { consortiaCentres = [] } = useConsortiaCenter();

  const api = useApi(process.env.REACT_APP_AMS_BASE_URL);
  const [isChecked, setIsChecked] = useState(false);
  const location = useLocation();
  const { subjectData } = useSubject();
  const { selectedSubject, selectedSubjectCode } = subjectData || {};
  const subjectId = subjectData?.selectedSubjectId;
  const {
    unit,
    sessionId,
    sessionStartDate,
    sessionEndDate,
    visitDate,
    statusType,
    additionalVisitDate = null,
  } = location.state || {};

  const { centerNumber, centerName, centres } = useCenter();
  const currentCentreType =
    getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, subjectId) || "";
  const navigate = useNavigate();
  const unitCode = location.state.unit;
  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "Unsaved changes will be lost. Are you sure you want to continue?",
      );
      if (!confirmLeave) return;
    }
    localStorage.removeItem(STORAGE_KEY);
    navigate(`/dashboard/marks_submission/${selectedSubject}`, {
      state: { unitCode, unit },
    });
  };

  const consortiaUnitcode =
    String(unitCode ?? "")
      .toUpperCase()
      .match(/^[A-Z0-9]+/)?.[0] || "";

  const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    consortiaUnitcode,
  );

  const handleSave = async () => {
    let updatedRows = isStage3
      ? rows.filter((val) => !val.disableCandidate)
      : rows;
    //  Save to localStorage only here
    localStorage.setItem("marksRows", JSON.stringify(updatedRows));
    const unitCode = location.state.unit;

    let payload = updatedRows.map((row) => {
      return {
        candidateNumber: Number(row.candidateNumber),
        markAsAbsent: Boolean(row.markAsAbsent),
        totalCriteriaAchieved: row.totalCriteriaAchieved ?? null, // default 0 if null
        assignmentId: row.assignmentId || null,
        markStatusId: MARK_STATUS.SAVED,
        centreNumber: Number(
          currentCentreType === "Lead" && isConsortiaUnit
            ? row.centreNumber || centerNumber
            : centerNumber,
        ),
        sessionId: sessionId,
        subjectId: Number(subjectId),
        assessmentCode: unitCode.split(" ")[0],
      };
    });

    try {
      const currentCentreType =
        getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, subjectId) || "";
      const saveEndpoint =
        currentCentreType === "Lead" && isConsortiaUnit
          ? "consortia/saveOrUpdateCandidateMarks"
          : "/saveOrUpdateCandidateMarks";

      const response = await api.post(saveEndpoint, payload);

      if (response.status === 200) {
        toast.success("Your changes have been saved");
        if (onManualSaveSuccess) onManualSaveSuccess(rows);
      } else {
        console.error("Failed to save marks", response);
      }
    } catch (error) {
      console.error("Error saving marks", error);
    }
  };

  const handleSubmit = () => {
    // Trigger your existing validation (and error toast) in the parent/other component
    if (onSubmitClicked) onSubmitClicked();

    // Use the provided isRowComplete to decide whether to open the modal
    // Supports both a boolean prop or a function prop
    const rowsToCheck = isStage3
      ? Array.isArray(rows)
        ? rows.filter((r) => !r?.disableCandidate)
        : []
      : rows;
    const complete =
      typeof isRowComplete === "function"
        ? isRowComplete(rowsToCheck)
        : Boolean(isRowComplete);

    if (!complete) {
      // Do NOT open the modal; the other component will show the error message
      return;
    }

    // Only when complete, proceed
    localStorage.removeItem("marksRows");
    setShowModal(true);
  };
  const getSessionDetails = (sessionStartDate, sessionEndDate, sessionId) => {
    const academicYear = `${new Date(sessionStartDate).getFullYear()}/${String(
      new Date(sessionEndDate).getFullYear(),
    ).slice(-2)}`;
    return sessionId === moderationWindowSession.window1
      ? `Window 1 (February series) - ${academicYear}`
      : sessionId === moderationWindowSession.window2
        ? `Window 2 (June series) - ${academicYear}`
        : "";
  };

  const onClose = () => {
    setShowModal(false);
    setIsChecked(false);
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return ""; // or return "N/A", depending on what you want

    const date = new Date(dateStr);
    if (isNaN(date)) return ""; // handle invalid date strings

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <MarkSubmissionConfirmModal
        isOpen={showModal}
        onClose={onClose}
        rows={rows}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
        type="marks"
        isStage3={isStage3}
        isPostStage3={isPostStage3}
      />
      <hr className="my-2" />
      <div
        className="xcard unit-header mt-2 xp-4 xhas-shadow-2 is-flex is-justify-content-space-between mb-4 is-align-items-center"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            textAlign: "left",
          }}
        >
          <div className="is-size-6">
            {(["Visit Confirmed"].includes(statusType) || isStage3) && (
              <>
                <strong>
                  Visit date:{" "}
                  { formatDate(visitDate)}
                </strong>{" "}
                |{" "}
              </>
            )}
            
            {statusType === "Visit Requested" && !isStage3 && (
              <>
                <strong>Visit requested</strong> |{" "}
              </>
            )}
            {getSessionDetails(sessionStartDate, sessionEndDate, sessionId)}
          </div>
<>
               {additionalVisitDate && (
                <div className="is-flex is-align-items-center">
                  <span className="icon mr-1">
                    <svg
                      style={{ fill: "#a55900" }}
                      xmlns="http://www.w3.org/2000/svg"
                      height="24px"
                      viewBox="0 -960 960 960"
                      width="24px"
                      fill="212121"
                    >
                      <path d="m560-120-57-57 144-143H200v-480h80v400h367L503-544l56-57 241 241-240 240Z" />
                    </svg>
                  </span>
                  <span className="pt-1">
                    <strong style={{ color: "#a55900" }}>
                      {`Additional visit: ${formatDate(additionalVisitDate)}`}
                    </strong>
                  </span>
                </div>
              )}
              </>
          <div className="is-size-5">
            <strong>{unit}</strong>
          </div>
        </div>
        <div>
          <div>
            <div className="field is-grouped">
              <button
                className="button is-text is-outlined mr-2"
                onClick={handleBack}
              >
                <span className="icon mr-2">
                  <svg
                    style={{ fill: "#0076BD" }}
                    xmlns="http://www.w3.org/2000/svg"
                    height="24"
                    viewBox="0 -960 960 960"
                    width="24"
                  >
                    <path d="M560-253.847 333.847-480 560-706.153 602.153-664l-184 184 184 184L560-253.847Z"></path>
                  </svg>
                </span>
                Back
              </button>
              <button
                className="button is-primary is-outlined mr-2"
                onClick={handleSave}
              >
                Save
              </button>
              <button
                className="button is-primary is-outlined mr-2"
                onClick={() => {
                  // Set the key based on isStage3
                  if (isStage3 || isPostStage3) {
                    handleDownloadStage3(
                      rows,
                      "UpdatedTotalMarks",
                      location,
                      { centerNumber, centerName },
                      subjectData,
                      api,
                      {},
                      isPostStage3,
                      currentCentreType,
                      isConsortiaUnit,
                      centres,
                      isStage3,
                      moderationWindowSession
                    );
                  } else {
                    handleDownload(
                      rows,
                      "totalMarks",
                      location,
                      { centerNumber, centerName },
                      subjectData,
                      api,
                      {},
                      currentCentreType,
                      isConsortiaUnit,
                      centres,
                      moderationWindowSession
                    );
                  }
                }}
              >
                Download
              </button>

              <button className="button is-primary" onClick={handleSubmit}>
                {isStage3 ? "Update marks" : "Submit marks"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MarkSubmissionHeader;
